import { getElement, getElements, toggleClass } from '../helpers/functions';
import { baseElements } from '../helpers/constants';
import { getScroll } from './smooth-scroll';
class DesktopNavigation {
  static SELECTORS = {
    LEVEL_ZERO_LINKS: '[data-level="0"]',
    MENU_LISTS: '.slide-menu__list:not(.__first)',
    CLOSE_BUTTON: '.slide-menu__closebutton',
    MENU_LINK: '.slide-menu__link',
    MENU_ITEMS: '.slide-menu__list-item'
  };
  static CLASSES = {
    OPEN: '__open',
    BODY_OVERFLOW: 'body-overflow'
  };
  static ANIMATION_DELAY = 500;
  constructor() {
    const {
      $html,
      $header
    } = baseElements;

    // Store DOM references
    this.html = $html;
    this.header = $header;
    this.lenis = getScroll();

    // Initialize state
    this.state = {
      activeMenu: null,
      isTransitioning: false
    };

    // Initialize DOM elements
    if (!this.initializeElements()) return;

    // Bind methods
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  /**
   * Initialize and validate required DOM elements
   * @returns {boolean} Whether initialization was successful
   */
  initializeElements() {
    const {
      SELECTORS
    } = DesktopNavigation;
    this.$elements = {
      $links: getElements(SELECTORS.LEVEL_ZERO_LINKS, this.header),
      $lists: getElements(SELECTORS.MENU_LISTS, this.header),
      $closeButton: getElement(SELECTORS.CLOSE_BUTTON, this.header)
    };
    return this.$elements.$links.length > 0 && this.$elements.$lists.length > 0 && this.$elements.$closeButton;
  }

  /**
   * Initialize event listeners
   */
  create() {
    this.$elements.$links.forEach($link => {
      $link.addEventListener('click', this.handleMenuToggle);
    });
    this.$elements.$lists.forEach($list => {
      $list.setAttribute('aria-hidden', 'true');
      $list.inert = true;
    });
    this.$elements.$closeButton.addEventListener('click', this.handleCloseClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    this.$elements.$links.forEach(link => {
      link.removeEventListener('click', this.handleMenuToggle);
    });
    this.$elements.$closeButton.removeEventListener('click', this.handleCloseClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.closeAllMenus();
    this.$elements.$lists.forEach($list => {
      $list.setAttribute('aria-hidden', 'false');
      $list.inert = false;
    });
  }

  /**
   * Handle menu toggle click event
   * @param {Event} event - Click event
   */
  handleMenuToggle(event) {
    const link = event.target.closest(DesktopNavigation.SELECTORS.MENU_LINK);
    if (!link) return;
    const menu = link.nextElementSibling;
    if (!DesktopNavigation.isValidMenu(menu)) return;
    event.preventDefault();
    this.toggleMenu(menu);
  }

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeydown(event) {
    if (event.key === 'Escape' && this.state.activeMenu) {
      this.closeAllMenus();
    }
  }

  /**
   * Validate if menu has items
   * @param {HTMLElement} menu - Menu element to validate
   * @returns {boolean} Whether menu is valid
   */
  static isValidMenu(menu) {
    if (!menu) return false;
    const menuItems = getElements(DesktopNavigation.SELECTORS.MENU_ITEMS, menu);
    return menuItems.length > 0;
  }

  /**
   * Toggle menu state
   * @param {HTMLElement} newMenu - Menu to toggle
   */
  toggleMenu(newMenu) {
    if (this.state.isTransitioning || newMenu === this.state.activeMenu) return;
    toggleClass(this.html, DesktopNavigation.CLASSES.BODY_OVERFLOW, true);
    toggleClass(this.header, DesktopNavigation.CLASSES.OPEN, true);
    if (this.state.activeMenu) {
      this.state.isTransitioning = true;
      toggleClass(this.state.activeMenu, DesktopNavigation.CLASSES.OPEN, false);
      this.state.activeMenu.setAttribute('aria-hidden', 'true');
      this.state.activeMenu.inert = true;
      setTimeout(() => {
        this.openMenu(newMenu);
        this.state.isTransitioning = false;
      }, DesktopNavigation.ANIMATION_DELAY);
    } else {
      this.openMenu(newMenu);
    }
  }

  /**
   * Open specified menu
   * @param {HTMLElement} menu - Menu to open
   */
  openMenu(menu) {
    toggleClass(menu, DesktopNavigation.CLASSES.OPEN, true);
    this.state.activeMenu = menu;
    this.state.activeMenu.setAttribute('aria-hidden', 'false');
    this.state.activeMenu.inert = false;
    this.lenis.stop();
  }

  /**
   * Handle close button click
   */
  handleCloseClick() {
    this.closeAllMenus();
  }

  /**
   * Close all menus and reset state
   */
  closeAllMenus() {
    this.$elements.$lists.forEach($list => {
      toggleClass($list, DesktopNavigation.CLASSES.OPEN, false);
      $list.setAttribute('aria-hidden', 'true');
      $list.inert = true;
    });
    setTimeout(() => {
      toggleClass(this.header, DesktopNavigation.CLASSES.OPEN, false);
      toggleClass(this.html, DesktopNavigation.CLASSES.BODY_OVERFLOW, false);
      this.state.isTransitioning = false;
    }, DesktopNavigation.ANIMATION_DELAY);
    this.lenis.start();
    this.state.activeMenu = null;
    this.state.isTransitioning = false;
  }
}
export default DesktopNavigation;